// components/MyProfile/Support/KnowledgeBaseSearch.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Paper,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Link,
    InputAdornment,
    Alert,
    Collapse,
    IconButton,
    Chip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { 
    Search, 
    ExternalLink, 
    BookOpen,
    ChevronDown,
    ChevronUp,
    Lightbulb
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDebounce } from '../../../hooks/useDebounce'
import zendeskService from '../../../services/zendeskService';
import { useAuth0 } from '@auth0/auth0-react';

const KnowledgeBaseSearch = ({ onArticleClick, embedded = false }) => {
    const theme = useTheme();
    const { getAccessTokenSilently } = useAuth0();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // State
    const [searchQuery, setSearchQuery] = useState('');
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedArticle, setExpandedArticle] = useState(null);
    const [showResults, setShowResults] = useState(false);
    
    // Debounce search query
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    
    // Common issues for quick access
    const commonIssues = [
        'How to add family members',
        'Port-in process guide',
        'Payment methods',
        'Cancel subscription',
        'Technical troubleshooting'
    ];
    
    // Search articles
    const searchArticles = async (query) => {
        if (!query || query.length < 3) {
            setArticles([]);
            setShowResults(false);
            return;
        }
        
        try {
            setLoading(true);
            setError(null);
            
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });
            
            const response = await zendeskService.searchKnowledgeBase({ query }, token);
            
            setArticles(response.data.articles);
            setShowResults(true);
            
        } catch (err) {
            console.error('Error searching knowledge base:', err);
            setError('Failed to search articles. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    
    // Effect for debounced search
    useEffect(() => {
        if (debouncedSearchQuery) {
            searchArticles(debouncedSearchQuery);
        } else {
            setArticles([]);
            setShowResults(false);
        }
    }, [debouncedSearchQuery]);
    
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };
    
    const handleCommonIssueClick = (issue) => {
        setSearchQuery(issue);
    };
    
    const handleArticleClick = (article) => {
        if (onArticleClick) {
            onArticleClick(article);
        }
    };
    
    const toggleArticleExpand = (articleId) => {
        setExpandedArticle(expandedArticle === articleId ? null : articleId);
    };
    
    const stripHtml = (html) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };
    
    if (embedded) {
        // Simplified version for embedding in form
        return (
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search for help articles..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search size={20} />
                            </InputAdornment>
                        ),
                        endAdornment: loading && (
                            <InputAdornment position="end">
                                <CircularProgress size={20} />
                            </InputAdornment>
                        )
                    }}
                    sx={{ mb: 2 }}
                />
                
                <AnimatePresence>
                    {showResults && articles.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                        >
                            <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 300, overflowY: 'auto' }}>
                                <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                                    Found {articles.length} related articles:
                                </Typography>
                                <List dense>
                                    {articles.slice(0, 3).map((article) => (
                                        <ListItem
                                            key={article.id}
                                            button
                                            component="a"
                                            href={article.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                borderRadius: 1,
                                                mb: 0.5,
                                                '&:hover': { bgcolor: 'action.hover' }
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                                            {article.title}
                                                        </Typography>
                                                        <ExternalLink size={14} />
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                                {articles.length > 3 && (
                                    <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                        + {articles.length - 3} more articles
                                    </Typography>
                                )}
                            </Paper>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>
        );
    }
    
    return (
        <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
        >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box sx={{ 
                    width: isMobile ? 64 : 80, 
                    height: isMobile ? 64 : 80, 
                    borderRadius: '50%', 
                    bgcolor: '#2196F3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 2
                }}>
                    <BookOpen size={isMobile ? 32 : 40} color="white" />
                </Box>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
                    Knowledge Base
                </Typography>
                <Typography color="textSecondary">
                    Search our help articles before submitting a ticket
                </Typography>
            </Box>
            
            {/* Search Input */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="What can we help you with?"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search size={24} />
                            </InputAdornment>
                        ),
                        endAdornment: loading && (
                            <InputAdornment position="end">
                                <CircularProgress size={24} />
                            </InputAdornment>
                        )
                    }}
                    sx={{ mb: 3 }}
                />
                
                {/* Common Issues */}
                <Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        <Lightbulb size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        Common topics:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {commonIssues.map((issue, index) => (
                            <Chip
                                key={index}
                                label={issue}
                                onClick={() => handleCommonIssueClick(issue)}
                                size="small"
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: 'primary.main',
                                        color: 'white'
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </Paper>
            
            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            
            {/* Search Results */}
            <AnimatePresence>
                {showResults && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                    >
                        {articles.length === 0 ? (
                            <Paper sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="h6" gutterBottom>
                                    No articles found
                                </Typography>
                                <Typography color="textSecondary">
                                    Try searching with different keywords or submit a support ticket
                                </Typography>
                            </Paper>
                        ) : (
                            <Paper>
                                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                    <Typography variant="h6">
                                        Found {articles.length} article{articles.length !== 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                                <List>
                                    {articles.map((article, index) => (
                                        <React.Fragment key={article.id}>
                                            {index > 0 && <Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}
                                            <ListItem
                                                sx={{
                                                    flexDirection: 'column',
                                                    alignItems: 'stretch',
                                                    py: 2
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                    <Typography 
                                                        variant="subtitle1" 
                                                        sx={{ 
                                                            fontWeight: 600,
                                                            cursor: 'pointer',
                                                            '&:hover': { color: 'primary.main' }
                                                        }}
                                                        onClick={() => toggleArticleExpand(article.id)}
                                                    >
                                                        {article.title}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleArticleExpand(article.id)}
                                                        >
                                                            {expandedArticle === article.id ? 
                                                                <ChevronUp size={20} /> : 
                                                                <ChevronDown size={20} />
                                                            }
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            component="a"
                                                            href={article.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <ExternalLink size={20} />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                                
                                                <Typography 
                                                    variant="body2" 
                                                    color="textSecondary"
                                                    sx={{ mb: expandedArticle === article.id ? 2 : 0 }}
                                                >
                                                    {stripHtml(article.body).substring(0, 150)}...
                                                </Typography>
                                                
                                                <Collapse in={expandedArticle === article.id}>
                                                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                        <Typography variant="body2" paragraph>
                                                            {stripHtml(article.body)}
                                                        </Typography>
                                                        <Link
                                                            href={article.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                                        >
                                                            Read full article
                                                            <ExternalLink size={16} />
                                                        </Link>
                                                    </Box>
                                                </Collapse>
                                            </ListItem>
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Paper>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
};

export default KnowledgeBaseSearch;